import { Container } from '@playcanvas/pcui';
import { Ray } from 'playcanvas';

import { raycastSceneMeshes } from './collision';
import { Events } from './events';
import { Scene } from './scene';
import { MenuItem, MenuPanel } from './ui/menu-panel';

const annotationTypes = ['pin', 'evidence', 'witness', 'vehicle', 'poi', 'damage'] as const;
type AnnotationType = (typeof annotationTypes)[number];

class CanvasContextMenu {
    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        // ---- Context menu (same pattern as scene-tree.ts) ----
        const menuPanel = new MenuPanel([]);
        document.body.appendChild(menuPanel.dom);
        menuPanel.dom.classList.add('canvas-context-menu');
        menuPanel.dom.style.position = 'fixed';
        menuPanel.dom.style.zIndex = '10000';

        // ---- Inline annotation editor ----
        const editor = document.createElement('div');
        editor.className = 'select-toolbar';
        editor.style.position = 'fixed';
        editor.style.display = 'none';
        editor.style.flexDirection = 'column';
        editor.style.gap = '8px';
        editor.style.padding = '8px';
        editor.style.height = 'auto';
        editor.style.zIndex = '10001';
        editor.style.borderRadius = '8px';
        document.body.appendChild(editor);

        const typeSelect = document.createElement('select');
        typeSelect.className = 'pcui-input';
        for (const type of annotationTypes) {
            const option = document.createElement('option');
            option.value = type;
            option.textContent = type;
            typeSelect.appendChild(option);
        }

        const labelInput = document.createElement('input');
        labelInput.className = 'pcui-input';
        labelInput.type = 'text';
        labelInput.placeholder = 'Annotation label';
        labelInput.maxLength = 256;

        const btnRow = document.createElement('div');
        btnRow.style.display = 'flex';
        btnRow.style.gap = '6px';

        const confirmBtn = document.createElement('button');
        confirmBtn.className = 'pcui-button select-toolbar-button';
        confirmBtn.textContent = 'Confirm';

        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'pcui-button select-toolbar-button';
        cancelBtn.textContent = 'Cancel';

        btnRow.appendChild(confirmBtn);
        btnRow.appendChild(cancelBtn);

        editor.appendChild(typeSelect);
        editor.appendChild(labelInput);
        editor.appendChild(btnRow);

        // Stop pointer events inside editor from propagating to the canvas
        editor.addEventListener('pointerdown', (e) => e.stopPropagation());

        let pendingPosition: [number, number, number] | null = null;

        const hideEditor = () => {
            editor.style.display = 'none';
            pendingPosition = null;
            labelInput.value = '';
            typeSelect.value = 'pin';
            confirmBtn.disabled = true;
        };

        const showEditor = (clientX: number, clientY: number, worldPos: [number, number, number]) => {
            pendingPosition = worldPos;
            editor.style.left = `${clientX}px`;
            editor.style.top = `${clientY}px`;
            editor.style.display = 'flex';
            confirmBtn.disabled = true;
            labelInput.focus();
        };

        const updateConfirmState = () => {
            confirmBtn.disabled = !pendingPosition || labelInput.value.trim().length === 0;
        };

        const confirmPlacement = () => {
            if (!pendingPosition) return;
            const label = labelInput.value.trim();
            if (!label) return;

            const typeValue = typeSelect.value as AnnotationType;
            const id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
                ? crypto.randomUUID()
                : `anno-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

            events.fire('annotations.add', {
                id,
                worldPosition: pendingPosition,
                type: typeValue,
                label,
                author: 'editor',
                createdAt: new Date().toISOString()
            });

            hideEditor();
        };

        labelInput.addEventListener('input', updateConfirmState);
        confirmBtn.addEventListener('click', confirmPlacement);
        cancelBtn.addEventListener('click', hideEditor);

        // Keyboard: Enter = confirm, Escape = cancel
        editor.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                confirmPlacement();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                hideEditor();
            }
        });

        // ---- Position picking (same approach as annotation-tool.ts) ----
        const pickWorldPosition = async (clientX: number, clientY: number): Promise<[number, number, number] | null> => {
            const canvas = scene.canvas;
            const rect = canvas.getBoundingClientRect();
            const screenX = clientX - rect.left;
            const screenY = clientY - rect.top;

            // Try mesh raycast first
            const ray = new Ray();
            scene.camera.getRay(screenX, screenY, ray);
            const hit = raycastSceneMeshes(scene, ray);
            if (hit) {
                return [hit.position.x, hit.position.y, hit.position.z];
            }

            // Fallback: camera intersection (splat / point cloud)
            // camera.intersect expects normalized 0-1 coordinates relative to the container.
            const containerDom = canvasContainer.dom;
            const containerRect = containerDom.getBoundingClientRect();
            const result = await scene.camera.intersect(
                (clientX - containerRect.left) / containerDom.clientWidth,
                (clientY - containerRect.top) / containerDom.clientHeight
            );
            if (result) {
                return [result.position.x, result.position.y, result.position.z];
            }

            return null;
        };

        // ---- Dismiss on outside click or any interaction ----
        const dismissMenu = () => {
            if (!menuPanel.hidden || editor.style.display !== 'none') {
                menuPanel.hidden = true;
                hideEditor();
            }
        };

        const outsideClickHandler = (event: PointerEvent) => {
            const target = event.target as Node;
            if (!menuPanel.dom.contains(target) && !editor.contains(target)) {
                dismissMenu();
            }
        };
        document.addEventListener('pointerdown', outsideClickHandler);

        // Scroll or wheel on canvas should also dismiss
        canvasContainer.dom.addEventListener('wheel', dismissMenu, { passive: true });

        // Any key press dismisses (e.g. Escape, or starting to type)
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                dismissMenu();
            }
        });

        const uiChromeSelector = [
            '#scene-panel',
            '#view-panel',
            '#color-panel',
            '#case-panel',
            '#evidence-panel',
            '#annotation-panel',
            '#bottom-toolbar',
            '#right-toolbar',
            '#mode-toggle',
            '#menu',
            '#timeline-panel',
            '#data-panel',
            '#status-bar',
            '#tooltips-container',
            '#top-container',
            '#popup',
            '#progress-container',
            '#spinner-container',
            'input',
            'textarea',
            'select',
            'button',
            'a'
        ].join(',');

        // ---- Context menu on the canvas ----
        canvasContainer.dom.addEventListener('contextmenu', async (e: MouseEvent) => {
            // Alt/Meta + right-click is used for camera zoom — don't show menu
            if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) {
                return;
            }

            const eventTarget = e.target as HTMLElement | null;
            const canvas = scene.canvas;
            const toolsContainer = canvasContainer.dom.querySelector('#tools-container');
            const isCanvasTarget = !!eventTarget && (
                eventTarget === canvas ||
                eventTarget === canvasContainer.dom ||
                !!toolsContainer?.contains(eventTarget)
            );

            if (!isCanvasTarget || eventTarget?.closest?.(uiChromeSelector)) {
                return;
            }

            e.preventDefault();
            e.stopImmediatePropagation();

            // Hide editor from any previous interaction
            hideEditor();

            const worldPos = await pickWorldPosition(e.clientX, e.clientY);

            const items: MenuItem[] = [];

            items.push({
                text: 'Add Annotation Here',
                isEnabled: () => !!worldPos,
                onSelect: () => {
                    if (!worldPos) return;
                    showEditor(e.clientX, e.clientY, worldPos);
                }
            });

            menuPanel.setItems(items);
            menuPanel.hidden = false;

            const style = menuPanel.dom.style;
            style.left = `${e.clientX}px`;
            style.top = `${e.clientY}px`;

            // Clamp after layout so it stays on-screen
            requestAnimationFrame(() => {
                if (menuPanel.hidden) return;
                const rect = menuPanel.dom.getBoundingClientRect();
                const pad = 6;
                const x = Math.min(e.clientX, window.innerWidth - rect.width - pad);
                const y = Math.min(e.clientY, window.innerHeight - rect.height - pad);
                style.left = `${Math.max(pad, x)}px`;
                style.top = `${Math.max(pad, y)}px`;
            });
        }, true);
    }
}

export { CanvasContextMenu };
